/* eslint-disable max-len */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Button, Stack } from '@mui/material';
// import { FaAngleRight } from 'react-icons/fa6';
import { ContentType, ContentTypeKey } from '../../types';
import contentGraphQLClient from '../../../actions/resource_graphql_actions/content_graphql_client';
import { useSelector } from 'react-redux';
import { useSearchParams, useParams } from 'react-router-dom';
import useResourceHelper from '../../../BusinessSettings/Components/RevisedResources/ResourcesManagement/Components/Reusable/ContentCards/useResourceHelper';
import useContentPermissions from '../../../BusinessSettings/Components/RevisedResources/ResourcesManagement/Components/Reusable/ContentCards/useContentPermissions';
import { ContentStatus, TimePeriod } from '../../../actions/resource_graphql_actions/content_graphql_types';
interface ButtonTabsProps {
  chip?: { type: ContentType; number?: string }[];
  // eslint-disable-next-line no-unused-vars
  handleChipData?: (type: ContentType) => void;
  chipDataType?: ContentType;
  type?: string;
  // New props using keys instead of ContentType values
  selected?: ContentTypeKey;
  onChange?: (selectedKey: ContentTypeKey) => void;
  categoryId?: string; // Add categoryId prop for filtering,
  noDataCallback?: (val?: boolean) => void;
}

const ButtonTabs: React.FC<ButtonTabsProps> = ({
	chip,
	handleChipData,
	chipDataType,
	type,
	selected,
	onChange,
	categoryId: categoryIdProp,
	noDataCallback
}) =>
{
	const [ showButtons, setShowButtons ] = useState(false);
	const [ isSelected, setIsSelected ] = useState(false);
	const [ contentCounts, setContentCounts ] = useState({
		allContent    : 0,
		articles      : 0,
		images        : 0,
		videos        : 0,
		podcasts      : 0,
		documents     : 0,
		playlists     : 0,
		categories    : 0,
		subcategories : 0
	});
	const [ isLoading, setIsLoading ] = useState(true);
	// eslint-disable-next-line no-undef
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);
	const [ searchParams ] = useSearchParams();
	const status = searchParams.get('status') ||
		(window.location.pathname.includes('resource/playlist')
			? 'playlists'
			: undefined);	// Get session data from Redux store for companyId
	const session = useSelector((state: any) => state?.mysession?.session);
	const companyId = useParams()?.companyId || session?.companyId || '';
	const { getContentStatus, isFavoritesList, isAdminView, getDateFilterValue } = useResourceHelper();

	const categoryId = useParams().categoryId;
	const isUncategorized = searchParams.get('status') === 'uncategorized';
	const uncategorizedId = useSelector((state: any) => state.myresource?.uncategorizedId);
	const isFavorites = useParams()?.type === 'favorites';
	// Fixed content types to display as string values
	const contentTypes = {
		all           : 'All' as ContentType,
		articles      : 'Articles' as ContentType,
		images        : 'Images' as ContentType,
		videos        : 'Videos' as ContentType,
		podcasts      : 'Podcasts' as ContentType,
		documents     : 'Documents' as ContentType,
		playlists     : 'Playlists' as ContentType,
		subcategories : 'Sub categories' as ContentType,
		categories 	  : 'Sub categories' as ContentType
	};

	// Maintain order for display - show all content types for viewing
	const contentTypeList: ContentTypeKey[] = useMemo(() =>
	{
		if (status === 'playlists')
		{
			return [ 'playlists' ]; // Always show playlists for viewing
		}
		else if (type === 'Continue')
		{
			return [ 'videos', 'podcasts' ];
		}
		else if (isFavorites)
		{
			return [ 'playlists', 'articles', 'images', 'videos', 'podcasts', 'documents' ];
		}
		else
		{
			return categoryId
				? [ 'subcategories', 'articles', 'images', 'videos', 'podcasts', 'documents' ]
				: [ 'articles', 'images', 'videos', 'podcasts', 'documents' ];
		}
	}, [ status, type ]);

	// Find the key for a given content type value
	const getKeyFromType = (typeValue: ContentType): ContentTypeKey =>
	{
		return (Object.entries(contentTypes).find(([ _, value ]) => value === typeValue)?.[0] as ContentTypeKey) || 'all';
	};

	// Use the controlled selected prop if provided, otherwise convert chipDataType to a key
	const selectedKey = selected !== undefined ? selected : getKeyFromType(chipDataType || 'All');

	useEffect(() =>
	{
		// Update isSelected based on whether a non-All button is selected
		setIsSelected(selectedKey !== 'all');
	}, [ selectedKey ]);

	// Fetch content counts
	useEffect(() =>
	{
		const fetchContentCounts = async () =>
		{
			if (!companyId) return;

			setIsLoading(true);
			try
			{
				// Add categoryId to filters if it exists
				// const filters = categoryId ? { categoryIds: [ categoryId ] } : undefined;

				const memberFilter = searchParams.get('memberFilter');
				const dateFilter = searchParams.get('dateFilter') as TimePeriod;
				const contentStatus = getContentStatus();

				// Create uppercase types array based on contentTypeList
				const typesMapping: Record<ContentTypeKey, string> = {
					articles      : 'ARTICLE',
					videos        : 'VIDEO',
					podcasts      : 'PODCAST',
					images        : 'IMAGE',
					documents     : 'DOCUMENT',
					playlists     : 'PLAYLIST',
					all           : 'ALL',
					categories    : 'CATEGORY',
					subcategories : 'CATEGORY'
				};

				const typesArray = contentTypeList.map((key) => typesMapping[key]);

				const filters = {
					...(contentStatus && { status: contentStatus as unknown as ContentStatus }),
					nested : true,
					// ...(categoryId && { categoryIds: [ categoryId ] }),
					...(isUncategorized
						? uncategorizedId && { categoryIds: [ uncategorizedId ] }
						: categoryId && { categoryIds: [ categoryId ] }),
					...(memberFilter && memberFilter !== 'all' && { createdBy: memberFilter }),
					...(isFavoritesList && { favorited: true }),
					timeRange : (dateFilter && dateFilter !== 'ALL_TIME') ? getDateFilterValue(dateFilter) : undefined,
					types     : typesArray
				};
				const result = await contentGraphQLClient.getSeparateContentCounts(
					companyId,
					isAdminView ? 'Configuration' as const : 'MyResource' as const,
					filters
				) as any;

				if (result.allContent === 0)
				{
					noDataCallback?.(true);
				}
				setContentCounts(result);
			}
			catch (error)
			{
				console.error('Error fetching content counts:', error);
			}
			finally
			{
				setIsLoading(false);
			}
		};

		![ 'trending', 'Continue', 'Latest', 'relative' ].includes(type as string) && fetchContentCounts();

		const handleContentStatusChange = (event: CustomEvent) =>
		{
			if ([ 'deleted', 'subcategoryCreated' ].includes(event.detail.newStatus))
			{
				fetchContentCounts();
			}
		};

		window.addEventListener('contentStatusChanged', handleContentStatusChange as any);
		window.addEventListener('subcategoryCreated', handleContentStatusChange as any);

		return () =>
		{
			window.removeEventListener('contentStatusChanged', handleContentStatusChange as any);
			window.removeEventListener('subcategoryCreated', handleContentStatusChange as any);
		};
	}, [ companyId, categoryId, searchParams, uncategorizedId ]);

	const handleSelect = (key: ContentTypeKey) =>
	{
		// Get the actual content type value from the key
		const typeValue = contentTypes[key];

		// Call the original handler with the ContentType value for backward compatibility
		handleChipData?.(typeValue);

		// Call the new handler with the key
		if (onChange)
		{
			onChange(key);
		}

		if (key === 'all')
		{
			setIsSelected(false);
		}
		else
		{
			setIsSelected(true);
		}
	};

	const handleMouseEnter = () =>
	{
		if (timeoutRef.current)
		{
			clearTimeout(timeoutRef.current);
			timeoutRef.current = null;
		}
		setShowButtons(true);
	};

	const handleMouseLeave = () =>
	{
		timeoutRef.current = setTimeout(() =>
		{
			if (!isSelected)
			{
				setShowButtons(false);
			}
		}, 300);
	};

	return (
		<Stack
			direction='row'
			spacing={1}
			sx={{ display: 'inline-flex' }}
		>
			{/* First button (All) */}
			<Button
				size='small'
				variant={selectedKey === 'all' ? 'contained' : 'outlined'}
				className={selectedKey === 'all' ? 'whiteTxt' : ''}
				onClick={() => handleSelect('all')}
				onMouseEnter={handleMouseEnter}
				// endIcon={<FaAngleRight style={{ fontSize: '14px' }}/>}
				onMouseLeave={handleMouseLeave}
				sx={{
					borderRadius           : 'var(--pixel20)',
					color                  : '#737373',
					lineHeight             : 1,
					padding                : '6px 10px 8px 10px',
					transition             : 'transform 0.3s ease-in-out',
					'& .MuiButton-endIcon' : {
						marginLeft : '4px',
						marginTop  : '1px'
					}
				}}
			>
				{contentTypes.all}&nbsp;
				{/* ({contentCounts.allContent}) */}
				{!isLoading && ![ 'trending', 'Continue', 'Latest', 'relative' ].includes(type as string) && ` (${contentCounts.allContent})`}
			</Button>

			{/* Remaining buttons (Articles, Images, Videos, Podcasts) */}
			{contentTypeList.map((key, index) =>
			{
				const contentType = contentTypes[key];
				const count = contentCounts[key];
				const isDisabled = !isLoading && count === 0;

				return (
					<Button
						key={index}
						size='small'
						variant={selectedKey === key ? 'contained' : 'outlined'}
						className={selectedKey === key ? 'whiteTxt' : ''}
						onClick={() => !isDisabled && handleSelect(key)}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
						disabled={isDisabled}
						sx={{
							borderRadius     : 'var(--pixel20)',
							color            : '#737373',
							lineHeight       : 1,
							padding          : '6px 10px 8px 10px',
							transition       : 'opacity 0.3s ease, transform 0.3s ease-in-out',
							opacity          : 1,
							transform        : 'translateX(0)',
							pointerEvents    : 'auto',
							'&.Mui-disabled' : {
								pointerEvents : 'allow'
							}
						}}
					>
						{contentType}
						{!isLoading && ![ 'trending', 'Continue', 'Latest', 'relative' ].includes(type as string) && ` (${count})`}
					</Button>
				);
			})}
		</Stack>
	);
};

export default ButtonTabs;